import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PaymentProviderConditions } from '../../../models/payments';
import { PaymentDetailsBanner } from '../../PaymentDirector/molecules/PaymentDetailsBanner/PaymentDetailsBanner';
import { hasSkipConsentScreen } from '../../../../../models/configuration';
import { updateCredentials as updateCredentialsConnect } from '../../../../../connectivity/services/credentials';
import { AppState } from '../../../../../stores/redux-store';
import { ERR_USER_CANCELLED } from '../../../../../utilities/error';
import { Provider, ProviderEntity } from '../../../../../models/provider';
import { applicationParameters } from '../../../../../utilities/application-parameters';
import { setApplicationError as setApplicationErrorConnect } from '../../../../../stores/app-state-store';
import { AwaitingCredentialsPay } from '../../../../../components/screens/AwaitingCredentialsScreen/AwaitingCredentialsPay';
import { useAnalytics } from '../../../../../core/Analytics/AnalyticsProvider';
import { PageView, UserInteractionEvent } from '../../../../../models/analytics';
import { AwaitingScenario } from '../../../../../models/credentials';
import { useConfiguration } from '../../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useVersion } from '../../../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import {
  InteractionElement,
  parentMessenger,
} from '../../../../../core/Analytics/ParentApplicationEventMessenger';
import { PersistedProviderInputsProvider } from '../../../../../components/screens/SubmitCredentials/PersistedProviderInputsProvider/PersistedProviderInputsProvider';
import { useSourceAccountProvidedSkipsAis } from '../../../hooks/useSourceAccountProvidedSkipsAis/useSourceAccountProvidedSkipsAis';
import { SubmitPayCredentials } from './organisms/SubmitPayCredentials/SubmitPayCredentials';
import {
  getPrefilledInputfields,
  SubmitPayCredentialsBankID,
} from './organisms/SubmitPayCredentialsBankID/SubmitPayCredentialsBankID';

export type SubmitCredentialsControllerOwnProps = {
  paymentDetailsBanner?: React.ReactNode;
  isPaymentSca: boolean;
  onExecute: VoidFunction | undefined;
  provider: ProviderEntity;
  autoSubmitCredentials?: boolean;
  isOneSca?: boolean;
};

type SubmitCredentialsControllerProps = PropsFromRedux & SubmitCredentialsControllerOwnProps;

const SubmitCredentialsControllerPresenter: React.FC<SubmitCredentialsControllerProps> = ({
  provider,
  session,
  transfer,
  paymentDetailsBanner,
  isPaymentSca,
  onExecute,
  providerPaymentConditions,
  setApplicationError,
  autoSubmitCredentials = false,
  isOneSca,
}) => {
  const {
    version: { version },
  } = useVersion();
  const history = useHistory();

  const { tinklinkAnalytics } = useAnalytics();
  const { data: configurationData } = useConfiguration();
  const username = applicationParameters.get('username');
  const personalIdentifier = session?.personalIdentifier;
  const isSkipAIS = useSourceAccountProvidedSkipsAis();
  const prefilledFields = getPrefilledInputfields({ provider, session });

  const sendEvent = (
    userInteractionEvent: UserInteractionEvent,
    interactionElement: InteractionElement
  ) => {
    tinklinkAnalytics.sendInteractionEvent({
      action: `${PageView.SUBMIT_CREDENTIALS_SCREEN}/${userInteractionEvent}`,
      view: PageView.SUBMIT_CREDENTIALS_SCREEN,
    });

    parentMessenger.fireInteractionEvent({
      pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
      element: interactionElement,
    });
  };

  const onRedirectTo = (redirectTo: string) => {
    if (applicationParameters.get('providerName')) {
      sendEvent(UserInteractionEvent.CLOSE_BUTTON, InteractionElement.CLOSE_BUTTON);

      setApplicationError(ERR_USER_CANCELLED);
    } else {
      history.push(redirectTo);
    }
  };

  const onHeaderButtonClick: VoidFunction = () => {
    if (applicationParameters.get('providerName')) {
      sendEvent(UserInteractionEvent.CLOSE_BUTTON, InteractionElement.CLOSE_BUTTON);

      setApplicationError(ERR_USER_CANCELLED);
    } else {
      history.goBack();
    }
  };

  const isPISOnly =
    Boolean(providerPaymentConditions) &&
    !PaymentProviderConditions.isSourceAccountRequiredBeforeRedirect(providerPaymentConditions);

  const awaitingScenario =
    transfer || isSkipAIS || isPISOnly || isPaymentSca
      ? AwaitingScenario.PAYMENT
      : AwaitingScenario.ACCOUNTS;
  const footer =
    awaitingScenario === AwaitingScenario.PAYMENT
      ? paymentDetailsBanner || <PaymentDetailsBanner />
      : null;

  const shouldTriggerBankId =
    Provider.isMobileBankId(provider) &&
    prefilledFields &&
    !Provider.isRedirectAuthenticationFlow(provider);

  if (isSkipAIS || hasSkipConsentScreen(configurationData)) {
    if (shouldTriggerBankId) {
      return (
        <SubmitPayCredentialsBankID
          provider={provider}
          awaitingScenario={awaitingScenario}
          footerDisclaimer={footer}
          fields={prefilledFields}
          onHeaderButtonClick={onHeaderButtonClick}
          isPaymentSca={isPaymentSca}
          onExecute={onExecute}
          isOneSca={isOneSca}
        />
      );
    }
  }

  return (
    <PersistedProviderInputsProvider
      providerFields={provider.fields}
      fixedValue={personalIdentifier || username || undefined}
    >
      <AwaitingCredentialsPay
        provider={provider}
        footerDisclaimer={footer}
        onExecute={onExecute}
        awaitingScenario={awaitingScenario}
        autoSubmitCredentials={autoSubmitCredentials}
        onBack={onHeaderButtonClick}
        isPaymentSca={isPaymentSca}
      >
        <SubmitPayCredentials
          onExecute={onExecute}
          version={version}
          footer={footer}
          provider={provider}
          onHeaderButtonClick={onHeaderButtonClick}
          onRedirectTo={onRedirectTo}
          autoSubmitCredentials={autoSubmitCredentials}
        />
      </AwaitingCredentialsPay>
    </PersistedProviderInputsProvider>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    transfer: state.transfer.data,
    session: state.session.data,
    providerPaymentConditions: state.selectedProvider.data.paymentConditions,
  };
};

const connector = connect(mapStateToProps, {
  updateCredentials: updateCredentialsConnect,
  setApplicationError: setApplicationErrorConnect,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SubmitCredentialsControllerPay = connector(SubmitCredentialsControllerPresenter);
